import React, { useEffect, useState } from "react";
import { useParams, Navigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import axios from "axios";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import BoardRoom from "./BoardRoom";

const GUEST_ID_KEY = "wb_guest_id";

const getGuestId = () => {
  let guestId = localStorage.getItem(GUEST_ID_KEY);
  if (!guestId) {
    guestId =
      window.crypto && window.crypto.randomUUID
        ? window.crypto.randomUUID()
        : `${Date.now()}-xxxx-4xxx-yxxx-xxxxxxxxxxxx`.replace(/[xy]/g, (c) => {
            const r = (Math.random() * 16) | 0;
            return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
          });
    localStorage.setItem(GUEST_ID_KEY, guestId);
  }
  return guestId;
};

const CenterCard = ({ children }) => (
  <Box sx={{ background: "#F8F8FA", minHeight: "70vh", padding: "60px 0" }}>
    <Container maxWidth="sm">
      <Paper elevation={2} sx={{ borderRadius: "20px", padding: "35px", textAlign: "center" }}>
        {children}
      </Paper>
    </Container>
  </Box>
);

const BoardJoin = () => {
  const { code } = useParams();
  const location = useLocation();
  const { t } = useTranslation();
  const token = useSelector((state) => state.user)?.token;

  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [joinData, setJoinData] = useState(null);
  const [guestName, setGuestName] = useState("");
  const [isJoining, setIsJoining] = useState(false);

  // the whiteboard experience is Arabic-first regardless of browser language
  useEffect(() => {
    if (i18next.language !== "ar") {
      i18next.changeLanguage("ar");
      document.dir = "rtl";
    }
  }, []);

  useEffect(() => {
    axios
      .post(`${process.env.REACT_APP_API_URL}/whiteboard/info`, { code })
      .then((response) => setInfo(response.data.data))
      .catch((err) => setError(err?.response?.data?.message ?? t("smth_went_wrong")));
  }, [code]);

  const joinAsStudent = () => {
    setIsJoining(true);
    axios
      .post(`${process.env.REACT_APP_API_URL}/whiteboard/join`, { code })
      .then((response) => setJoinData(response.data.data))
      .catch((err) => setError(err?.response?.data?.message ?? t("smth_went_wrong")))
      .finally(() => setIsJoining(false));
  };

  const joinAsGuest = (event) => {
    event.preventDefault();
    if (!guestName.trim()) return;
    setIsJoining(true);
    axios
      .post(`${process.env.REACT_APP_API_URL}/whiteboard/join-public`, {
        code,
        display_name: guestName.trim(),
        guest_id: getGuestId(),
      })
      .then((response) => setJoinData(response.data.data))
      .catch((err) => setError(err?.response?.data?.message ?? t("smth_went_wrong")))
      .finally(() => setIsJoining(false));
  };

  // logged-in students join automatically once the session info is confirmed
  useEffect(() => {
    if (info && token && !joinData && !isJoining && info.status === "live") {
      joinAsStudent();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [info, token]);

  if (joinData) {
    return <BoardRoom joinData={joinData} />;
  }

  if (error) {
    return (
      <CenterCard>
        <Typography variant="h6">{error}</Typography>
      </CenterCard>
    );
  }

  if (!info) {
    return (
      <CenterCard>
        <CircularProgress />
      </CenterCard>
    );
  }

  if (info.visibility === "private" && !token) {
    return <Navigate to="/sign-in" state={{ from: location.pathname }} />;
  }

  if (info.status !== "live") {
    return (
      <CenterCard>
        <Typography variant="h5" mb={1}>{info.title}</Typography>
        <Typography color="text.secondary">{t("wb_not_live")}</Typography>
      </CenterCard>
    );
  }

  if (token) {
    return (
      <CenterCard>
        <CircularProgress />
      </CenterCard>
    );
  }

  return (
    <CenterCard>
      <Typography variant="h5" mb={1}>{info.title}</Typography>
      <Typography color="text.secondary" mb={3}>{t("wb_enter_name")}</Typography>
      <form onSubmit={joinAsGuest}>
        <TextField
          fullWidth
          value={guestName}
          onChange={(event) => setGuestName(event.target.value)}
          placeholder={t("wb_your_name")}
          inputProps={{ maxLength: 40 }}
          sx={{ marginBottom: "20px" }}
        />
        <Button
          type="submit"
          fullWidth
          variant="contained"
          size="large"
          disabled={isJoining || !guestName.trim()}
          sx={{ borderRadius: "100vh" }}
        >
          {t("wb_join")}
        </Button>
      </form>
    </CenterCard>
  );
};

export default BoardJoin;
